import { useEffect, useMemo, useState } from "react";
import { Mail, RefreshCw, Send, Eye, AlertTriangle, CheckCircle2, Image as ImageIcon, Upload, Play, Clock, X, Save, ChevronLeft, ChevronRight } from "lucide-react";

type Track = "investor" | "banker";

// Field names mirror the DB columns exactly (see lib/db/src/schema/email-drips.ts).
interface Template {
  id: number;
  track: Track;
  day: number;
  subject: string;
  introText: string;
  valuePropText: string;
  featureKey: string;
  featureName: string;
  featureBlurb: string;
  screenshotKey: string;
  ctaLabel: string;
  ctaPath: string;
  tomorrowTeaser: string;
  inventoryHash: string;
  generatedAt: string;
  generationModel: string | null;
}

interface Subscription {
  id: number; subjectType: Track; subjectId: number; email: string; name: string;
  track: Track; status: string; currentDay: number; enrolledAt: string;
  nextSendDueAt: string | null; completedAt: string | null;
  unsubscribedAt: string | null; isTest: boolean;
  sends: Array<{ id: number; day: number; status: string; sentAt: string; subject: string; errorMessage: string | null }>;
}

interface Screenshot { key: string; objectPath: string; publicUrl: string; updatedAt: string }

const ADMIN = () => localStorage.getItem("admin_token") || "";

async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", "x-admin-token": ADMIN(), ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  return res.json();
}

export default function AdminOnboardingDrips() {
  const [tab, setTab] = useState<Track>("investor");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorDay, setEditorDay] = useState(1);

  const [testEmail, setTestEmail] = useState("");
  const [testName, setTestName] = useState("");

  const reload = async () => {
    setLoading(true); setError(null);
    const [tRes, sRes, scRes] = await Promise.allSettled([
      api<{ templates: Template[] }>("/onboarding-drips/templates"),
      api<{ subscriptions: Subscription[] }>("/onboarding-drips/subscriptions"),
      api<{ screenshots: Screenshot[] }>("/onboarding-drips/screenshots"),
    ]);

    const failures: string[] = [];
    if (tRes.status === "fulfilled") {
      setTemplates(tRes.value.templates || []);
    } else {
      failures.push(`templates: ${tRes.reason?.message || tRes.reason}`);
    }
    if (sRes.status === "fulfilled") {
      setSubs(sRes.value.subscriptions || []);
    } else {
      failures.push(`subscriptions: ${sRes.reason?.message || sRes.reason}`);
    }
    if (scRes.status === "fulfilled") {
      setScreenshots(scRes.value.screenshots || []);
    } else {
      failures.push(`screenshots: ${scRes.reason?.message || scRes.reason}`);
    }
    if (failures.length > 0) {
      setError(`Partial load failure — ${failures.join(" | ")}`);
    }
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const dayTemplates = useMemo(() =>
    templates.filter((t) => t.track === tab).sort((a, b) => a.day - b.day),
    [templates, tab],
  );

  const screenshotMap = useMemo(() => {
    const m = new Map<string, Screenshot>();
    for (const s of screenshots) m.set(s.key, s);
    return m;
  }, [screenshots]);

  const trackSubs = useMemo(() => subs.filter((s) => s.track === tab), [subs, tab]);

  const handleRegenerate = async () => {
    setBusy("regenerate"); setError(null); setInfo(null);
    try {
      const r = await api("/onboarding-drips/regenerate", {
        method: "POST",
        body: JSON.stringify({ track: tab, force: true }),
      });
      setInfo(`Regenerated ${tab} track: ${JSON.stringify(r.results?.[0])}`);
      await reload();
    } catch (err: any) {
      setError(`Regenerate failed: ${err?.message}`);
    }
    setBusy(null);
  };

  const handleSendTest = async () => {
    if (!testEmail || !testEmail.includes("@")) { setError("Valid email required for test send"); return; }
    setBusy("send-test"); setError(null); setInfo(null);
    try {
      const r = await api("/onboarding-drips/send-test", {
        method: "POST",
        body: JSON.stringify({ track: tab, toEmail: testEmail.trim(), toName: testName.trim() }),
      });
      setInfo(`Test send complete — ${r.sentCount}/10 succeeded for ${testEmail}`);
      await reload();
    } catch (err: any) {
      setError(`Send-test failed: ${err?.message}`);
    }
    setBusy(null);
  };

  const handleRunTick = async () => {
    setBusy("tick"); setError(null); setInfo(null);
    try {
      const r = await api("/onboarding-drips/run-tick", { method: "POST" });
      setInfo(`Tick: enrolled ${r.newlyEnrolled}, advanced ${r.advanced}, completed ${r.completed}, errors ${r.errors}`);
      await reload();
    } catch (err: any) {
      setError(`Tick failed: ${err?.message}`);
    }
    setBusy(null);
  };

  const handleScreenshotUpload = async (key: string, file: File) => {
    setBusy(`upload-${key}`); setError(null); setInfo(null);
    try {
      const presign = await api<{ uploadUrl: string; objectPath: string }>(`/onboarding-drips/screenshots/${encodeURIComponent(key)}/upload-url`, {
        method: "POST",
        body: JSON.stringify({ contentType: file.type || "image/png" }),
      });
      const putRes = await fetch(presign.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "image/png" },
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      await api(`/onboarding-drips/screenshots/${encodeURIComponent(key)}/finalize`, {
        method: "POST",
        body: JSON.stringify({ objectPath: presign.objectPath }),
      });
      setInfo(`Screenshot updated: ${key}`);
      await reload();
    } catch (err: any) {
      setError(`Upload failed: ${err?.message}`);
    }
    setBusy(null);
  };

  // Patch a single template back to the server. Used by the modal editor.
  const handleSaveTemplate = async (id: number, fields: Partial<Template>): Promise<Template> => {
    const r = await api<{ template: Template }>(`/onboarding-drips/templates/${id}`, {
      method: "PATCH",
      body: JSON.stringify(fields),
    });
    setTemplates((prev) => prev.map((p) => (p.id === id ? r.template : p)));
    return r.template;
  };

  const openEditor = (day: number) => { setEditorDay(day); setEditorOpen(true); };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif tracking-tight">Onboarding email drip</h1>
          <p className="text-sm text-muted-foreground mt-1">10-email sequence sent to every new {tab} after first login. D1, D2, D3, then every 48h — about 16 calendar days end-to-end.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRunTick}
            disabled={!!busy}
            className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-md border border-border hover:bg-muted transition disabled:opacity-50"
            title="Manually run the daily 2 PM IST cron (idempotent)"
          >
            <Play className="w-3.5 h-3.5" /> Run tick now
          </button>
          <button
            onClick={reload}
            disabled={loading || !!busy}
            className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-md border border-border hover:bg-muted transition disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {(["investor", "banker"] as const).map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t ? "border-[#FF5C00] text-[#FF5C00]" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t === "investor" ? "Investors" : "Advisors (Bankers)"}
            <span className="ml-2 text-xs text-muted-foreground">({templates.filter((tm) => tm.track === t).length}/10)</span>
          </button>
        ))}
      </div>

      {(error || info) && (
        <div className={`p-3 rounded-md text-sm border ${error ? "bg-red-500/10 border-red-500/30 text-red-700" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-700"}`}>
          {error || info}
        </div>
      )}

      {/* Generation controls */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border p-4">
          <h2 className="text-sm font-semibold flex items-center gap-2"><Mail className="w-4 h-4" /> Content</h2>
          <p className="text-xs text-muted-foreground mt-1">Regenerate runs the AI grounded in CODEBASE_INVENTORY. Or click any day below to edit a single email by hand.</p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              onClick={handleRegenerate}
              disabled={busy === "regenerate"}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md bg-[#FF5C00] text-white hover:bg-[#FF5C00]/90 disabled:opacity-50"
            >
              {busy === "regenerate" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Force regenerate {tab} track
            </button>
            <button
              onClick={() => openEditor(1)}
              disabled={dayTemplates.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md border border-[#FF5C00] text-[#FF5C00] hover:bg-[#FF5C00]/5 disabled:opacity-50"
              title="Open the full editor — review and edit all 10 emails"
            >
              <Eye className="w-3.5 h-3.5" /> View & edit all 10
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-border p-4">
          <h2 className="text-sm font-semibold flex items-center gap-2"><Send className="w-4 h-4" /> Send a test (all 10 to one address)</h2>
          <p className="text-xs text-muted-foreground mt-1">Subject prefixed with [TEST D&#123;n&#125;]. No throttling — fires immediately.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="email@example.com"
              className="px-2 py-1.5 text-sm rounded-md border border-border bg-background" />
            <input value={testName} onChange={(e) => setTestName(e.target.value)} placeholder="Name (optional)"
              className="px-2 py-1.5 text-sm rounded-md border border-border bg-background" />
          </div>
          <button
            onClick={handleSendTest}
            disabled={busy === "send-test" || !testEmail}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md bg-[#FF5C00] text-white hover:bg-[#FF5C00]/90 disabled:opacity-50"
          >
            {busy === "send-test" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send all 10
          </button>
        </div>
      </div>

      {/* Templates list — clicking any row opens the modal at that day. */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/30">
          <h2 className="text-sm font-semibold">{tab === "investor" ? "Investor" : "Advisor"} drip — 10 emails (click any day to edit)</h2>
        </div>
        {dayTemplates.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <AlertTriangle className="w-5 h-5 mx-auto mb-2" />
            No templates generated yet. Click "Force regenerate" above.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {dayTemplates.map((t) => {
              const screenshot = screenshotMap.get(t.screenshotKey);
              return (
                <li key={t.id}>
                  <button
                    onClick={() => openEditor(t.day)}
                    className="w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors flex items-start gap-3"
                  >
                    <div className="w-10 h-10 rounded-md bg-[#FF5C00]/10 text-[#FF5C00] flex items-center justify-center font-mono text-sm font-semibold shrink-0">D{t.day}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{t.subject}</p>
                        {screenshot ? (
                          <span title={`screenshot: ${t.screenshotKey}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded bg-emerald-500/10 text-emerald-700">
                            <ImageIcon className="w-3 h-3" /> {t.screenshotKey}
                          </span>
                        ) : (
                          <span title="No screenshot uploaded yet" className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded bg-amber-500/10 text-amber-700">
                            <ImageIcon className="w-3 h-3" /> {t.screenshotKey} (missing)
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{t.introText}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">CTA: {t.ctaLabel} → {t.ctaPath}</p>
                    </div>
                    <div className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-border text-muted-foreground">
                      <Eye className="w-3 h-3" /> Edit
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Subscriptions */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Live subscriptions ({trackSubs.length})</h2>
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Cron fires daily at 2:00 PM IST</p>
        </div>
        {trackSubs.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No subscriptions yet for {tab}s.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/20 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">Recipient</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Day</th>
                <th className="text-left px-4 py-2">Next due</th>
                <th className="text-left px-4 py-2">Sends</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {trackSubs.map((s) => {
                const okSends = s.sends.filter((x) => x.status === "sent").length;
                const failedSends = s.sends.filter((x) => x.status === "failed").length;
                return (
                  <tr key={s.id} className={s.isTest ? "bg-amber-500/5" : ""}>
                    <td className="px-4 py-2">
                      <div className="font-medium">{s.name || s.email}</div>
                      <div className="text-xs text-muted-foreground">{s.email} {s.isTest && <span className="ml-1 px-1 py-0.5 rounded bg-amber-500/20 text-amber-700 text-[9px]">TEST</span>}</div>
                    </td>
                    <td className="px-4 py-2">
                      <StatusPill status={s.status} />
                    </td>
                    <td className="px-4 py-2 font-mono">D{s.currentDay}/10</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{s.nextSendDueAt ? new Date(s.nextSendDueAt).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2 text-xs">
                      <span className="text-emerald-700">{okSends} sent</span>
                      {failedSends > 0 && <span className="text-red-700 ml-2">{failedSends} failed</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editorOpen && dayTemplates.length > 0 && (
        <DripEditorModal
          track={tab}
          templates={dayTemplates}
          screenshotMap={screenshotMap}
          startDay={editorDay}
          onClose={() => setEditorOpen(false)}
          onSave={handleSaveTemplate}
          onUploadScreenshot={handleScreenshotUpload}
        />
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, { bg: string; fg: string; icon?: any }> = {
    active: { bg: "bg-emerald-500/10", fg: "text-emerald-700", icon: CheckCircle2 },
    completed: { bg: "bg-blue-500/10", fg: "text-blue-700", icon: CheckCircle2 },
    unsubscribed: { bg: "bg-zinc-500/10", fg: "text-zinc-700" },
    paused: { bg: "bg-amber-500/10", fg: "text-amber-700" },
  };
  const c = map[status] || { bg: "bg-zinc-500/10", fg: "text-zinc-700" };
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${c.bg} ${c.fg}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {status}
    </span>
  );
}

// ---------- Editor modal ----------
//
// Full-screen modal that lets the admin walk through all 10 emails for a track,
// edit any field (subject, intro, value-prop, feature copy, CTA, teaser,
// screenshot key), and save back to the server. Live preview iframe on the
// right re-loads after save so the admin sees their edit immediately.

interface EditorProps {
  track: Track;
  templates: Template[]; // already filtered + sorted for the active track
  screenshotMap: Map<string, Screenshot>;
  startDay: number;
  onClose: () => void;
  onSave: (id: number, fields: Partial<Template>) => Promise<Template>;
  onUploadScreenshot: (key: string, file: File) => Promise<void>;
}

const EDITABLE_FIELDS: Array<{ key: keyof Template; label: string; multiline: boolean; hint?: string }> = [
  { key: "subject", label: "Subject line", multiline: false, hint: "Shows in the inbox preview." },
  { key: "introText", label: "Intro paragraph", multiline: true, hint: "Top of the email — sets context for the day's topic." },
  { key: "valuePropText", label: "Value prop paragraph", multiline: true, hint: "Why this matters to the recipient." },
  { key: "featureName", label: "Feature name", multiline: false, hint: "Highlighted heading inside the feature block." },
  { key: "featureBlurb", label: "Feature blurb", multiline: true, hint: "1–2 sentences describing the highlighted feature." },
  { key: "screenshotKey", label: "Screenshot key", multiline: false, hint: "Anchor key for the inline screenshot. Upload a matching image below." },
  { key: "ctaLabel", label: "CTA button label", multiline: false },
  { key: "ctaPath", label: "CTA path", multiline: false, hint: "Relative path inside the app, e.g. /investor/portal." },
  { key: "tomorrowTeaser", label: "Tomorrow teaser", multiline: true, hint: "One-liner at the bottom hinting at the next day's email." },
];

function DripEditorModal({ track, templates, screenshotMap, startDay, onClose, onSave, onUploadScreenshot }: EditorProps) {
  const initialIdx = Math.max(0, templates.findIndex((t) => t.day === startDay));
  const [idx, setIdx] = useState(initialIdx);
  const current = templates[idx];

  const [draft, setDraft] = useState<Partial<Template>>(current);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [previewBust, setPreviewBust] = useState(0);

  // Reset draft when switching days.
  useEffect(() => {
    setDraft(current);
    setSaveErr(null);
    setSavedAt(null);
  }, [current?.id]);

  // Detect dirty state to warn when the admin moves between days unsaved.
  const isDirty = useMemo(() => {
    if (!current) return false;
    return EDITABLE_FIELDS.some(({ key }) => (draft as any)[key] !== (current as any)[key]);
  }, [draft, current]);

  const goto = (newIdx: number) => {
    if (isDirty && !confirm("You have unsaved changes on this day. Discard them?")) return;
    setIdx(Math.max(0, Math.min(templates.length - 1, newIdx)));
  };

  const save = async () => {
    if (!current) return;
    setSaving(true); setSaveErr(null);
    try {
      const fields: Partial<Template> = {};
      for (const { key } of EDITABLE_FIELDS) {
        const v = (draft as any)[key];
        if (v !== (current as any)[key]) (fields as any)[key] = v;
      }
      if (Object.keys(fields).length === 0) { setSaveErr("Nothing changed"); setSaving(false); return; }
      const updated = await onSave(current.id, fields);
      setDraft(updated);
      setSavedAt(Date.now());
      setPreviewBust((n) => n + 1);
    } catch (err: any) {
      setSaveErr(err?.message || "Save failed");
    }
    setSaving(false);
  };

  if (!current) return null;
  const screenshot = screenshotMap.get(draft.screenshotKey || current.screenshotKey);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-background rounded-lg border border-border w-full max-w-7xl h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/20">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-md bg-[#FF5C00]/10 text-[#FF5C00] flex items-center justify-center font-mono text-sm font-semibold shrink-0">D{current.day}</div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold truncate">{track === "investor" ? "Investor" : "Advisor"} drip — Day {current.day} of 10</h2>
              <p className="text-xs text-muted-foreground truncate">{current.subject}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => goto(idx - 1)}
              disabled={idx === 0}
              className="p-1.5 rounded-md border border-border hover:bg-muted disabled:opacity-30"
              title="Previous day"
            ><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-xs text-muted-foreground tabular-nums">{idx + 1} / {templates.length}</span>
            <button
              onClick={() => goto(idx + 1)}
              disabled={idx === templates.length - 1}
              className="p-1.5 rounded-md border border-border hover:bg-muted disabled:opacity-30"
              title="Next day"
            ><ChevronRight className="w-4 h-4" /></button>
            <button
              onClick={onClose}
              className="ml-1 p-1.5 rounded-md border border-border hover:bg-muted"
              title="Close (Escape)"
            ><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Body: editor on left, live preview on right */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 overflow-hidden">
          {/* Editor */}
          <div className="overflow-y-auto p-5 border-r border-border">
            <div className="space-y-4">
              {EDITABLE_FIELDS.map(({ key, label, multiline, hint }) => {
                const value = (draft as any)[key] ?? "";
                const onChange = (v: string) => setDraft((d) => ({ ...d, [key]: v }));
                return (
                  <div key={String(key)}>
                    <label className="block text-xs font-medium text-foreground mb-1">{label}</label>
                    {multiline ? (
                      <textarea
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        rows={key === "valuePropText" || key === "featureBlurb" || key === "introText" ? 4 : 2}
                        className="w-full px-3 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-[#FF5C00]/40"
                      />
                    ) : (
                      <input
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-[#FF5C00]/40"
                      />
                    )}
                    {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
                  </div>
                );
              })}

              {/* Screenshot upload */}
              <div className="rounded-md border border-border p-3 bg-muted/10">
                <div className="flex items-start gap-3">
                  <div className="w-32 h-20 rounded bg-muted flex items-center justify-center overflow-hidden shrink-0">
                    {screenshot ? (
                      <img src={screenshot.publicUrl} alt={screenshot.key} className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium">Inline screenshot for this email</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 break-all">key: {draft.screenshotKey}</p>
                    <label className="mt-2 cursor-pointer inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-border hover:bg-muted">
                      <Upload className="w-3.5 h-3.5" />
                      {screenshot ? "Replace image" : "Upload image"}
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (f && draft.screenshotKey) {
                          await onUploadScreenshot(draft.screenshotKey, f);
                          setPreviewBust((n) => n + 1);
                        }
                        e.target.value = "";
                      }} />
                    </label>
                  </div>
                </div>
              </div>

              {saveErr && (
                <div className="p-2 rounded-md text-xs border bg-red-500/10 border-red-500/30 text-red-700">{saveErr}</div>
              )}
            </div>
          </div>

          {/* Live preview */}
          <div className="overflow-hidden flex flex-col bg-muted/20">
            <div className="px-4 py-2 border-b border-border bg-background flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Live preview {isDirty && <span className="text-amber-700">(unsaved edits not shown — save to refresh)</span>}</p>
              {savedAt && Date.now() - savedAt < 5000 && (
                <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Saved</span>
              )}
            </div>
            <iframe
              key={previewBust}
              title={`Preview ${track} D${current.day}`}
              src={`/api/onboarding-drips/preview/${track}/${current.day}?token=${encodeURIComponent(ADMIN())}&v=${previewBust}`}
              className="flex-1 w-full bg-white"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border bg-muted/20 flex items-center justify-between">
          <p className="text-[11px] text-muted-foreground">Editing field-by-field. Changes are saved as plain text and re-rendered into HTML on every send.</p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded-md border border-border hover:bg-muted"
            >Close</button>
            <button
              onClick={save}
              disabled={saving || !isDirty}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-[#FF5C00] text-white hover:bg-[#FF5C00]/90 disabled:opacity-50"
            >
              {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
